import { signal, computed, type Signal } from "@preact/signals-react";
import type { ActiveGame, RoomConnectionStatus, RoomStateContainer } from "../room.types";
import type { Mate } from "@merels/room/types";
import { DEFAULT_HOST_TERM, type ChallengeConfiguration, type GameSetup, type SetupReadiness, type GameSavedOptions, type HostPlays } from "@merels/room";

export interface RoomStateInternal {
  // ... (interface remains same)
  // Connection
  status: Signal<RoomConnectionStatus>;
  roomId: Signal<string | null>;
  error: Signal<string | null>;
  roomName: Signal<string | null>;
  ownerName: Signal<string | null>;
  imageUrl: Signal<string | null>;
  ownerId: Signal<string | null>;
  videoEnabled: Signal<boolean>;
  isClosed: Signal<boolean>;
  opensAt: Signal<string | null>;
  /** Whether the room is currently in "gameless" mode (game hidden, video-only). Applies to all mates in the room. */
  gameless: Signal<boolean>;
  /** Mates who coordinate the room (owner-designated). */
  hostMateIds: Signal<string[]>;
  /** Whether the room is in managed mode: only hosts start or end games. */
  managed: Signal<boolean>;
  /** What the room calls a host, such as "Teacher". */
  hostTerm: Signal<string>;
  /** Each game's Host plays setting where the owner saved one. */
  hostPlaysByGameId: Signal<Record<string, HostPlays>>;
  /** Whether participants see only the owner's curated games. */
  curated: Signal<boolean>;
  /** The owner's ordered selection of games for a curated room. */
  curatedGameIds: Signal<string[]>;

  // Data
  activeGame: Signal<ActiveGame | null>;
  mates: Signal<Mate[]>;
  isLoading: Signal<boolean>;
  gameSetup: Signal<GameSetup | null>;
  setupReadiness: Signal<SetupReadiness | null>;
  savedOptions: Signal<GameSavedOptions | null>;
  /** Sticky release channel preference for the current game (see @merels/room's setGameChannelPreference), or null if unset/unknown. */
  gameChannelPreference: Signal<string | null>;
  challengeConfigurationPreference: Signal<ChallengeConfiguration | null>;

  /** The Omni mate this client acts as while the room has Omni mates (kept in step by room/init). */
  actingOmniMateId: Signal<string | undefined>;

  // Derived
  localMates: Signal<Mate[]>;
  /** The host mate this client acts for: the Omni mate it acts as if that one hosts, otherwise any of its host mates. */
  localHostMateId: Signal<string | undefined>;
  /** Whether this client acts for a host mate. */
  isLocalHost: Signal<boolean>;
  /** Whether this client may start, configure, or end games: always in an unmanaged room, and in a managed one only when one of its real (non-Omni) mates is a host. */
  canControlGames: Signal<boolean>;
  localAIPlayerIDs: Signal<Set<string>>;
  /** Room-wide: true while any mate in the room is a fake Omni mate (see Mate.isOmniMate), regardless of which client added them. Use this (not a per-client mode flag) for UI that must look the same to every connected client. */
  hasOmniMates: Signal<boolean>;
  
  // Internal
  pendingLocalMates: Mate[] | null;
}

export function makeRoomState(_container: RoomStateContainer, clientId: string): RoomStateInternal {
  const status = signal<RoomConnectionStatus>("disconnected");
  const roomId = signal<string | null>(null);
  const error = signal<string | null>(null);
  const roomName = signal<string | null>(null);
  const ownerName = signal<string | null>(null);
  const imageUrl = signal<string | null>(null);
  const ownerId = signal<string | null>(null);
  const videoEnabled = signal<boolean>(true);
  const isClosed = signal<boolean>(false);
  const opensAt = signal<string | null>(null);
  const gameless = signal<boolean>(false);
  const hostMateIds = signal<string[]>([]);
  const managed = signal<boolean>(false);
  const hostTerm = signal<string>(DEFAULT_HOST_TERM);
  const hostPlaysByGameId = signal<Record<string, HostPlays>>({});
  const actingOmniMateId = signal<string | undefined>(undefined);
  const curated = signal<boolean>(false);
  const curatedGameIds = signal<string[]>([]);

  const activeGame = signal<ActiveGame | null>(null);
  const mates = signal<Mate[]>([]);
  const isLoading = signal<boolean>(false);
  const gameSetup = signal<GameSetup | null>(null);
  const setupReadiness = signal<SetupReadiness | null>(null);
  const savedOptions = signal<GameSavedOptions | null>(null);
  const gameChannelPreference = signal<string | null>(null);
  const challengeConfigurationPreference = signal<ChallengeConfiguration | null>(null);

  const localMates = computed(() => {
    return mates.value.filter((m: any) => m.clientId === clientId);
  });

  const localAIPlayerIDs = computed(() => {
    const ids = new Set<string>();
    localMates.value.forEach(m => {
      if (m.isAI) ids.add(m.mateId);
    });
    return ids;
  });

  // In Omni mode the client takes the perspective of one Omni mate at a time,
  // so only that mate's host status counts.
  const localHostMateId = computed(() => {
    const acting = actingOmniMateId.value;
    if (acting) return hostMateIds.value.includes(acting) ? acting : undefined;
    return localMates.value.find((m) => hostMateIds.value.includes(m.mateId))?.mateId;
  });
  const isLocalHost = computed(() => localHostMateId.value !== undefined);
  // Starting, configuring, and closing games follow this client's real mates,
  // not the Omni mate it acts as: the room authorizes those per client.
  const canControlGames = computed(() =>
    !managed.value || localMates.value.some((m: any) => !m.isOmniMate && hostMateIds.value.includes(m.mateId))
  );

  const hasOmniMates = computed(() => mates.value.some((m: any) => m.isOmniMate));

  return {
    status,
    roomId,
    error,
    activeGame,
    mates,
    isLoading,
    gameSetup,
    setupReadiness,
    savedOptions,
    gameChannelPreference,
    challengeConfigurationPreference,
    localMates,
    localHostMateId,
    isLocalHost,
    canControlGames,
    localAIPlayerIDs,
    hasOmniMates,
    roomName,
    ownerName,
    imageUrl,
    ownerId,
    videoEnabled,
    isClosed,
    opensAt,
    gameless,
    hostMateIds,
    managed,
    hostTerm,
    hostPlaysByGameId,
    actingOmniMateId,
    curated,
    curatedGameIds,
    pendingLocalMates: null
  };
}
